import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { VersionNavigation } from "@/components/VersionNavigation";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "../../context/UserContext";
import { PageJumper } from "../../components/PageJumper";

// Helper function to get CSRF token from meta tags
const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        console.error("CSRF token vagy param meta tag nem található!");
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

const Translate = () => {
    const { gameId, versionId, fileId } = useParams();
    const queryClient = useQueryClient();
    const { user } = useUser();
    const { toast } = useToast();

    const [suggestionsPage, setSuggestionsPage] = useState(1);
    const [geminiResults, setGeminiResults] = useState(new Map<number, { assessment: string; suggestion: string }>());
    const [expandedRows, setExpandedRows] = useState(new Set<number>());
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingContent, setEditingContent] = useState<string>('');
    const [resolvedFileId, setResolvedFileId] = useState<string | undefined>(fileId);

    // Lekérjük az összes fájlt
    const { data: allVersionFiles } = useQuery({
        queryKey: ["allVersionFiles", gameId, versionId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files`);
            if (!response.ok) {
                throw new Error("Hálózati válasz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!gameId && !!versionId,
    });

    // Lekérjük az összes EN fájlt a verzióban - minden fájl, javaslatszámmal
    const { data: filesWithSuggestions } = useQuery({
        queryKey: ["filesWithSuggestions", gameId, versionId],
        queryFn: async () => {
            if (!allVersionFiles) return [];

            // Lekérjük az összes fájl javaslatszámát
            const filesWithCounts = await Promise.all(
                allVersionFiles.map(async (file: any) => {
                    try {
                        const suggestionsResponse = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files/${file.id}/suggestions?page=1`);
                        if (suggestionsResponse.ok) {
                            const suggestionsData = await suggestionsResponse.json();
                            // Az API teljes száma: total_count, ha van; vagy fallback a suggestions array hossza
                            const count = suggestionsData.total_count || suggestionsData.suggestions?.length || 0;
                            return {
                                ...file,
                                suggestion_count: count
                            };
                        }
                    } catch (e) {
                        // Hiba esetén 0 javaslatnak tekintjük
                    }
                    return { ...file, suggestion_count: 0 };
                })
            );

            // Visszatérünk ÖSSZES fájllal a javaslatszámukkal
            return filesWithCounts;
        },
        enabled: !!gameId && !!versionId && !!allVersionFiles,
    });

    // Állítsd be az első fileId-t, ha fileId undefined
    useEffect(() => {
        if (!fileId && !resolvedFileId) {
            // Ha már vannak fájlok javaslatokkal, használd az elsőt
            if (filesWithSuggestions && filesWithSuggestions.length > 0) {
                setResolvedFileId(filesWithSuggestions[0].id.toString());
            } else if (allVersionFiles && allVersionFiles.length > 0) {
                // Fallback: ha nincsenek javaslatok, az első fájlt használd
                setResolvedFileId(allVersionFiles[0].id.toString());
            }
        }
    }, [filesWithSuggestions, allVersionFiles, fileId, resolvedFileId]);

    const { data: translationFile, isLoading: _isLoadingFile } = useQuery({
        queryKey: ["translationFile", gameId, versionId, resolvedFileId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files/${resolvedFileId}`);
            if (!response.ok) {
                throw new Error("Hálózati válasz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!resolvedFileId,
    });

    const { data: gameDetails, isLoading: _isLoadingGame } = useQuery({
        queryKey: ["gameDetails", gameId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}`);
            if (!response.ok) {
                throw new Error("Hálózati válasz a játékhoz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!gameId,
    });

    const { data: versionDetails, isLoading: _isLoadingVersion } = useQuery({
        queryKey: ["versionDetails", gameId, versionId],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}`);
            if (!response.ok) {
                throw new Error("Hálózati válasz a verzióhoz nem megfelelő");
            }
            return response.json();
        },
        enabled: !!gameId && !!versionId,
    });

    const { data: suggestionsData, isLoading: isLoadingSuggestions } = useQuery({
        queryKey: ["suggestions", gameId, versionId, resolvedFileId, suggestionsPage],
        queryFn: async () => {
            const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files/${resolvedFileId}/suggestions?page=${suggestionsPage}`);
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            return response.json();
        },
        enabled: !!resolvedFileId,
    });

    // Ha az aktuális oldal már nem létezik (pl. az utolsó elemet elfogadtuk), ugorjunk az előző oldalra
    useEffect(() => {
        if (suggestionsData && suggestionsData.total_pages > 0 && suggestionsPage > suggestionsData.total_pages) {
            setSuggestionsPage(Math.max(1, suggestionsData.total_pages));
        } else if (suggestionsData && suggestionsData.total_pages === 0 && suggestionsPage > 1) {
            setSuggestionsPage(1);
        }
    }, [suggestionsData, suggestionsPage]);

    const acceptTranslationMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/extracted_texts/${translationId}/accept_translation`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({}),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Fordítás elfogadása sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["suggestions", gameId, versionId, resolvedFileId] });
            toast({ title: "Fordítás sikeresen elfogadva!" });
        },
        onError: (error) => {
            alert(`Hiba a fordítás elfogadása során: ${error.message}`);
        }
    });

    const updateTranslationMutation = useMutation({
        mutationFn: async ({ translationId, content }: { translationId: number; content: string }) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ extracted_text: { content: content } }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Fordítás frissítése sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["suggestions", gameId, versionId, resolvedFileId] });
            setEditingId(null);
            setEditingContent('');
            toast({ title: "Fordítás sikeresen frissítve!" });
        },
        onError: (error) => {
            toast({ title: "Fordítás frissítése sikertelen!", description: error.message, variant: "destructive" });
        }
    });

    const deleteTranslationMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: "DELETE",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Fordítás törlése sikertelen");
            }

            return translationId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["suggestions", gameId, versionId, resolvedFileId] });
            toast({ title: "Fordítás sikeresen törölve!" });
        },
        onError: (error) => {
            alert(`Hiba a fordítás törlése során: ${error.message}`);
        },
    });

    const geminiCheckMutation = useMutation({
        mutationFn: async ({ suggestionId, sourceText, targetText }: { suggestionId: number; sourceText: string; targetText: string }) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/gemini/check_translation`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ source_text: sourceText, target_text: targetText }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Gemini API hívás sikertelen");
            }
            return { suggestionId, result: await response.json() };
        },
        onSuccess: ({ suggestionId, result }) => {
            // Handle both old string format and new JSON format
            let assessment = '';
            let suggestion = '';

            console.log('Gemini result:', result); // Debug log

            // Helper function to extract JSON from markdown code blocks
            const extractJSON = (str: string): any | null => {
                try {
                    // Remove markdown code block markers: ```json\n...\n```
                    const jsonMatch = str.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
                    if (jsonMatch && jsonMatch[1]) {
                        return JSON.parse(jsonMatch[1]);
                    }
                    // Try direct JSON parse
                    return JSON.parse(str);
                } catch (e) {
                    return null;
                }
            };

            // Helper function to parse JSON strings
            const tryParseJSON = (value: any): { assessment: string; suggestion: string } | null => {
                if (typeof value === 'object' && value.assessment) {
                    return { assessment: value.assessment, suggestion: value.suggestion || '' };
                }
                if (typeof value === 'string') {
                    // First try to extract from markdown code block
                    const extracted = extractJSON(value);
                    if (extracted && typeof extracted === 'object' && extracted.assessment) {
                        return { assessment: extracted.assessment, suggestion: extracted.suggestion || '' };
                    }
                    // If still not found, the string itself might be the assessment
                    if (value && value.trim()) {
                        return { assessment: value.trim(), suggestion: '' };
                    }
                }
                return null;
            };

            // Try parsing in order of likelihood
            let parsed = tryParseJSON(result.result);
            if (!parsed) parsed = tryParseJSON(result);

            if (parsed) {
                assessment = parsed.assessment;
                suggestion = parsed.suggestion;
            } else {
                // Fallback: convert to string
                assessment = typeof result === 'string' ? result : JSON.stringify(result);
            }

            console.log('Assessment:', assessment, 'Suggestion:', suggestion); // Debug log

            setGeminiResults(prev => new Map(prev).set(suggestionId, { assessment, suggestion }));

            // Auto-expand the row to show the result
            setExpandedRows(prev => new Set(prev).add(suggestionId));

            toast({ title: "Gemini ellenőrzés sikeres!" });
        },
        onError: (error, { suggestionId }) => {
            setGeminiResults(prev => new Map(prev).set(suggestionId, { assessment: `Hiba: ${error.message}`, suggestion: '' }));
            toast({ title: "Gemini ellenőrzés sikertelen!", description: error.message, variant: "destructive" });
        },
    });

    const geminiCheckAllMutation = useMutation({
        mutationFn: async ({ translations }: { translations: { id: number; source_text: string; target_text: string }[] }) => {
            const { token } = getCsrfToken();
            if (!token) {
                throw new Error("CSRF token nem található.");
            }

            const response = await fetch(`/api/gemini/check_all_translations`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ translations: translations }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Gemini API hívás sikertelen");
            }
            return response.json();
        },
        onSuccess: (data) => {
            const newResults = new Map<number, { assessment: string; suggestion: string }>();
            const newExpandedIds = new Set<number>();

            console.log('Gemini bulk results:', data); // Debug log

            // Helper function to extract JSON from markdown code blocks
            const extractJSON = (str: string): any | null => {
                try {
                    // Remove markdown code block markers: ```json\n...\n```
                    const jsonMatch = str.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
                    if (jsonMatch && jsonMatch[1]) {
                        return JSON.parse(jsonMatch[1]);
                    }
                    // Try direct JSON parse
                    return JSON.parse(str);
                } catch (e) {
                    return null;
                }
            };

            // Handle both old format (string values) and new format (object with assessment/suggestion)
            for (const id in data.results) {
                const result = data.results[id];
                const idNum = parseInt(id, 10);

                let assessment = '';
                let suggestion = '';

                // Helper function to parse JSON strings
                const tryParseJSON = (value: any): { assessment: string; suggestion: string } | null => {
                    if (typeof value === 'object' && value.assessment) {
                        return { assessment: value.assessment, suggestion: value.suggestion || '' };
                    }
                    if (typeof value === 'string') {
                        // First try to extract from markdown code block
                        const extracted = extractJSON(value);
                        if (extracted && typeof extracted === 'object' && extracted.assessment) {
                            return { assessment: extracted.assessment, suggestion: extracted.suggestion || '' };
                        }
                        // If still not found, the string itself might be the assessment
                        if (value && value.trim()) {
                            return { assessment: value.trim(), suggestion: '' };
                        }
                    }
                    return null;
                };

                // Try parsing
                const parsed = tryParseJSON(result);
                if (parsed) {
                    assessment = parsed.assessment;
                    suggestion = parsed.suggestion;
                } else {
                    assessment = typeof result === 'string' ? result : JSON.stringify(result);
                    suggestion = '';
                }

                newResults.set(idNum, { assessment, suggestion });

                // Auto-expand rows with results
                newExpandedIds.add(idNum);
            }

            console.log('Processed newResults:', Array.from(newResults.entries())); // Debug log

            setGeminiResults(prev => new Map([...prev, ...newResults]));
            setExpandedRows(prev => new Set([...prev, ...newExpandedIds]));

            toast({ title: "Gemini tömeges ellenőrzés sikeres!" });
        },
        onError: (error) => {
            toast({ title: "Gemini tömeges ellenőrzés sikertelen!", description: error.message, variant: "destructive" });
        },
    });

    return (
        <div className="container mx-auto p-4 space-y-8 bg-background text-foreground">
            <Breadcrumb className="mb-4">
                <BreadcrumbList>
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to="/games">Játékok</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}`}>{gameDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}/versions/${versionId}`}>Verzió: {versionDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>Fájl: {translationFile?.name}</BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <VersionNavigation gameId={gameId} versionId={versionId} />
            <Card>
                <CardHeader>
                    <CardTitle>Tömeges Elfogadás</CardTitle>
                    <CardDescription>
                        Itt láthatod az összes beküldött fordítási javaslatot ehhez a fájlhoz. Elfogadhatod vagy törölheted őket.
                    </CardDescription>
                </CardHeader>
                {filesWithSuggestions === undefined ? (
                    <div className="px-6 py-3 border-b bg-yellow-50 text-yellow-800 text-sm">
                        Fájlok betöltése...
                    </div>
                ) : filesWithSuggestions && filesWithSuggestions.length > 0 ? (
                    <div className="px-6 py-3 border-b bg-muted/50">
                        <div className="flex flex-wrap gap-2 items-center">
                            <span className="text-sm font-medium text-muted-foreground">EN Fájlok javaslatokkal ({filesWithSuggestions.length}):</span>
                            {filesWithSuggestions.map((file: any) => (
                                <Button
                                    key={file.id}
                                    variant={resolvedFileId === file.id.toString() ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => {
                                        setResolvedFileId(file.id.toString());
                                        setSuggestionsPage(1);
                                    }}
                                    className="whitespace-nowrap"
                                >
                                    {file.name} ({file.suggestion_count})
                                </Button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="px-6 py-3 border-b bg-blue-50 text-blue-800 text-sm">
                        Nincs EN fájl javaslatokkal ebben a verzióban.
                    </div>
                )}
                <CardContent>
                    {!user ? (
                        <div className="text-center text-destructive text-base my-8">
                            Ehhez a funkcióhoz be kell jelentkezned.
                        </div>
                    ) : isLoadingSuggestions ? (
                        <p>Javaslatok betöltése...</p>
                    ) : suggestionsData && suggestionsData.suggestions.length > 0 ? (
                        <>
                            {/* Top Paginator */}
                            <div className="mb-6">
                                <PageJumper
                                    currentPage={suggestionsData.current_page}
                                    totalPages={suggestionsData.total_pages}
                                    onPageChange={setSuggestionsPage}
                                />
                            </div>

                            <div className="space-y-4">
                                {/* Header section */}
                                <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                                    <div className="flex-1">
                                        <h3 className="font-semibold">Angol (Eredeti) / Magyar (Javaslat)</h3>
                                    </div>
                                    {user?.admin && (
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    if (suggestionsData?.suggestions) {
                                                        // Csak azokat az elemeket küldjük el, amelyeknek nincs még Gemini ellenőrzése
                                                        const translations = suggestionsData.suggestions
                                                            .filter((s: any) => !geminiResults.has(s.id))
                                                            .map((s: any) => ({
                                                                id: s.id,
                                                                source_text: s.parent_text?.content || '',
                                                                target_text: s.content || '',
                                                            }));
                                                        if (translations.length > 0) {
                                                            geminiCheckAllMutation.mutate({ translations });
                                                        } else {
                                                            toast({
                                                                title: "Nincs mit ellenőrizni",
                                                                description: "Minden elemnek már van Gemini ellenőrzése.",
                                                                variant: "default"
                                                            });
                                                        }
                                                    }
                                                }}
                                                disabled={geminiCheckAllMutation.isPending || !suggestionsData?.suggestions || suggestionsData.suggestions.length === 0 || (suggestionsData?.suggestions?.filter((s: any) => !geminiResults.has(s.id))?.length === 0)}
                                            >
                                                {geminiCheckAllMutation.isPending ? 'Ellenőrzés...' : `Mindet ellenőrizd (${suggestionsData?.suggestions?.filter((s: any) => !geminiResults.has(s.id))?.length || 0} / ${suggestionsData?.suggestions?.length || 0})`}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    if (suggestionsData?.suggestions) {
                                                        // Csak az első 3 ellenőrizhetetlen elemet küldjük el
                                                        const translations = suggestionsData.suggestions
                                                            .filter((s: any) => !geminiResults.has(s.id))
                                                            .slice(0, 3)
                                                            .map((s: any) => ({
                                                                id: s.id,
                                                                source_text: s.parent_text?.content || '',
                                                                target_text: s.content || '',
                                                            }));
                                                        if (translations.length > 0) {
                                                            geminiCheckAllMutation.mutate({ translations });
                                                        } else {
                                                            toast({
                                                                title: "Nincs mit ellenőrizni",
                                                                description: "Minden elemnek már van Gemini ellenőrzése.",
                                                                variant: "default"
                                                            });
                                                        }
                                                    }
                                                }}
                                                disabled={geminiCheckAllMutation.isPending || !suggestionsData?.suggestions || suggestionsData.suggestions.length === 0 || (suggestionsData?.suggestions?.filter((s: any) => !geminiResults.has(s.id))?.length === 0)}
                                            >
                                                {geminiCheckAllMutation.isPending ? 'Ellenőrzés...' : `3-t ellenőrizd (${Math.min(3, suggestionsData?.suggestions?.filter((s: any) => !geminiResults.has(s.id))?.length || 0)})`}
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Suggestions cards */}
                                {suggestionsData.suggestions.map((suggestion: any, index: number) => {
                                    const isExpanded = expandedRows.has(suggestion.id);
                                    const cardBgColor = index % 2 === 0
                                        ? 'bg-blue-50 dark:bg-slate-900'
                                        : 'bg-gray-100 dark:bg-slate-800';
                                    const cardBorderColor = index % 2 === 0
                                        ? 'border-blue-200 dark:border-blue-900'
                                        : 'border-gray-300 dark:border-gray-700';

                                    return (
                                        <div key={suggestion.id} className={`border-2 rounded-lg p-4 ${cardBgColor} ${cardBorderColor}`}>
                                            {/* Main translation content */}
                                            <div className="space-y-3">
                                                {/* Expand button with EN/HU side by side */}
                                                <div className="flex items-start gap-3">
                                                    <button
                                                        onClick={() => {
                                                            setExpandedRows(prev => {
                                                                const next = new Set(prev);
                                                                if (next.has(suggestion.id)) {
                                                                    next.delete(suggestion.id);
                                                                } else {
                                                                    next.add(suggestion.id);
                                                                }
                                                                return next;
                                                            });
                                                        }}
                                                        className="p-1 hover:bg-gray-300 dark:hover:bg-gray-600 rounded flex-shrink-0 mt-1"
                                                        title={isExpanded ? "Bezárás" : "Megnyitás"}
                                                    >
                                                        <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </button>

                                                    {/* English and Hungarian side by side */}
                                                    <div className="flex-1 grid grid-cols-2 gap-4">
                                                        {/* English (Source) */}
                                                        <div>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-semibold">Angol (Eredeti)</p>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <p className="text-base text-gray-900 dark:text-gray-100 font-medium">{suggestion.parent_text?.content}</p>
                                                                <Link
                                                                    to={`/games/${gameId}/versions/${versionId}/translate?highlight_id=${suggestion.parent_text?.id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="h-5 w-5 p-0 inline-flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 rounded flex-shrink-0"
                                                                    title="Ugrás az ismétlődésekhez új ablakban"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                    </svg>
                                                                </Link>
                                                            </div>
                                                        </div>

                                                        {/* Hungarian (Target) */}
                                                        <div>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide font-semibold">Magyar (Javaslat)</p>
                                                            <div className="mt-1">
                                                                {editingId === suggestion.id ? (
                                                                    <textarea
                                                                        value={editingContent}
                                                                        onChange={(e) => setEditingContent(e.target.value)}
                                                                        className="w-full p-2 border rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                                                                        rows={8}
                                                                    />
                                                                ) : (
                                                                    <p className="text-base text-gray-900 dark:text-gray-100 break-words">{suggestion.content}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* File info and action buttons */}
                                                <div className="flex items-center justify-between pt-2 border-t border-current border-opacity-20">
                                                    <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                                        {suggestion.parent_text?.name && (
                                                            <>
                                                                <span>📄</span>
                                                                <span className="font-mono truncate">{suggestion.parent_text.name}</span>
                                                                <span className="text-gray-500 dark:text-gray-500">({suggestion.user?.username || 'N/A'})</span>
                                                                {suggestion.status === 'accepted' && (
                                                                    <span className="ml-2 px-2 py-1 bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100 rounded text-xs font-semibold">✓ Elfogadva</span>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-2">
                                                        {editingId === suggestion.id ? (
                                                            <>
                                                                <Button
                                                                    variant="default"
                                                                    size="sm"
                                                                    onClick={() => updateTranslationMutation.mutate({
                                                                        translationId: suggestion.id,
                                                                        content: editingContent
                                                                    })}
                                                                    disabled={updateTranslationMutation.isPending}
                                                                >
                                                                    {updateTranslationMutation.isPending ? 'Mentés...' : 'Mentés'}
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setEditingId(null);
                                                                        setEditingContent('');
                                                                    }}
                                                                    disabled={updateTranslationMutation.isPending}
                                                                >
                                                                    Mégse
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {suggestion.status !== 'accepted' && (user?.admin || user?.translater_admin) && (
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => acceptTranslationMutation.mutate(suggestion.id)}
                                                                        disabled={acceptTranslationMutation.isPending}
                                                                    >
                                                                        {acceptTranslationMutation.isPending ? "Elfogadás..." : "Elfogadás"}
                                                                    </Button>
                                                                )}
                                                                {(user?.admin || user?.translater_admin || user?.id === suggestion.user?.id) && (
                                                                    <>
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() => {
                                                                                setEditingId(suggestion.id);
                                                                                setEditingContent(suggestion.content);
                                                                            }}
                                                                        >
                                                                            Szerkesztés
                                                                        </Button>
                                                                        {(user?.admin || user?.translater_admin) && (
                                                                            <Button
                                                                                variant="destructive"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    if (window.confirm("Biztosan törölni szeretnéd ezt a javaslatot?")) {
                                                                                        deleteTranslationMutation.mutate(suggestion.id);
                                                                                    }
                                                                                }}
                                                                                disabled={deleteTranslationMutation.isPending}
                                                                            >
                                                                                {deleteTranslationMutation.isPending ? "Törlés..." : "Törlés"}
                                                                            </Button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Gemini check section */}
                                            {isExpanded && (
                                                <div className="mt-4 pt-4 border-t border-current border-opacity-20">
                                                    <h4 className="font-semibold mb-3 text-gray-900 dark:text-gray-100">Gemini Ellenőrzés</h4>
                                                    {geminiResults.has(suggestion.id) ? (
                                                        <div className="space-y-3">
                                                            <div className="bg-white dark:bg-slate-700 p-3 rounded border">
                                                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Értékelés:</p>
                                                                <p className="text-sm text-gray-900 dark:text-gray-100">
                                                                    {geminiResults.get(suggestion.id)?.assessment || '(üres)'}
                                                                </p>
                                                            </div>
                                                            {geminiResults.get(suggestion.id)?.suggestion && (
                                                                <div className="bg-white dark:bg-slate-700 p-3 rounded border border-blue-200 dark:border-blue-800">
                                                                    <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">Javítási javaslat:</p>
                                                                    <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                                                                        {geminiResults.get(suggestion.id)?.suggestion}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => geminiCheckMutation.mutate({
                                                                suggestionId: suggestion.id,
                                                                sourceText: suggestion.parent_text?.content || '',
                                                                targetText: suggestion.content || '',
                                                            })}
                                                            disabled={geminiCheckMutation.isPending && geminiCheckMutation.variables?.suggestionId === suggestion.id}
                                                        >
                                                            {geminiCheckMutation.isPending && geminiCheckMutation.variables?.suggestionId === suggestion.id ? 'Ellenőrzés folyamban...' : 'Ellenőrzés indítása'}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Bottom Paginator */}
                            <div className="mt-6">
                                <PageJumper
                                    currentPage={suggestionsData.current_page}
                                    totalPages={suggestionsData.total_pages}
                                    onPageChange={setSuggestionsPage}
                                />
                            </div>
                        </>
                    ) : (
                        <p>Nincsenek beküldött javaslatok ebben a fájlban.</p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Translate;
